/*
 * Sticky header.
 *
 * Previously lived in a section-scoped javascript block in sections/header.liquid,
 * which Shopify compiles into the theme-wide compiled_assets/scripts.js bundle.
 * That bundle does not hot-reload under `shopify theme dev` and, when it is stale or
 * truncated, every custom element defined in it silently fails to register.
 * Shipping it as a normal asset avoids that.
 */
(() => {
  if (customElements.get('sticky-header')) return;

  class StickyHeader extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.header = document.querySelector('.section-header');
      this.headerWrapper = document.querySelector('.header-wrapper');
      this.headerIsAlwaysSticky =
        this.getAttribute('data-sticky-type') === 'always' ||
        this.getAttribute('data-sticky-type') === 'reduce-logo-size';
      this.headerBounds = {};

      this.setHeaderHeight();

      window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

      if (this.headerIsAlwaysSticky) {
        this.header.classList.add('shopify-section-header-sticky');
      }

      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearch = this.querySelector('predictive-search');
      this.isTransitioning = false;

      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, false);

      this.createObserver();
    }

    setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
    }

    createObserver() {
      let observer = new IntersectionObserver((entries, observer) => {
        this.headerBounds = entries[0].intersectionRect;
        observer.disconnect();
      });

      observer.observe(this.header);
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (this.predictiveSearch && this.predictiveSearch.isOpen) return;
      if (this.isTransitioning) return;

      // Логіка для мобільних (менше або дорівнює 990px)
      if (window.innerWidth <= 990) {
        // If the drawer menu is open, don't toggle the scrolled state.
        // The drawer header layout should stay stable while menu is open.
        if (this.header.classList.contains('menu-open')) {
          this.currentScrollTop = scrollTop;
          return;
        }

        if (scrollTop > 100 && !this.header.classList.contains('scrolled-past-header')) {
          this.isTransitioning = true;
          this.header.classList.add('scrolled-past-header');
          this.header.classList.add('shopify-section-header-sticky');
          setTimeout(() => { this.isTransitioning = false; }, 200);
        } else if (scrollTop < 10 && this.header.classList.contains('scrolled-past-header')) {
          this.isTransitioning = true;
          this.header.classList.remove('scrolled-past-header');
          this.header.classList.remove('shopify-section-header-sticky');
          setTimeout(() => { this.isTransitioning = false; }, 200);
        }
        this.currentScrollTop = scrollTop;
        return;
      }

      const openMenuDetails = this.header.querySelector('details[open]');
      if (openMenuDetails || (this.headerWrapper && this.headerWrapper.preventHide)) {
        this.currentScrollTop = scrollTop;
        return;
      }

      // Логіка перемикання станів (тільки для десктопа)
      if (scrollTop > 100 && !this.header.classList.contains('scrolled-past-header')) {
        this.isTransitioning = true;
        this.header.classList.add('scrolled-past-header');
        this.header.classList.add('shopify-section-header-sticky');
        setTimeout(() => { this.isTransitioning = false; }, 200);
      } else if (scrollTop < 10 && this.header.classList.contains('scrolled-past-header')) {
        this.isTransitioning = true;
        this.header.classList.remove('scrolled-past-header');
        this.header.classList.remove('shopify-section-header-sticky');
        setTimeout(() => { this.isTransitioning = false; }, 200);
      }

      // Якщо вже згорнутий — не використовуємо стандартну логіку ховання Dawn
      if (this.header.classList.contains('scrolled-past-header')) {
        this.currentScrollTop = scrollTop;
        return;
      }

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        if (this.preventHide) return;
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.clearTimeout(this.isScrolling);

          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 66);

          requestAnimationFrame(this.hide.bind(this));
        }
      } else if (scrollTop <= this.headerBounds.top) {
        requestAnimationFrame(this.reset.bind(this));
      }

      this.currentScrollTop = scrollTop;
    }

    hide() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeMenuDisclosure();
      this.closeSearchModal();
    }

    reveal() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
    }

    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }

    closeMenuDisclosure() {
      this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
      this.disclosures.forEach((disclosure) => disclosure.close());
    }

    closeSearchModal() {
      this.searchModal = this.searchModal || this.header.querySelector('details-modal');
      this.searchModal.close(false);
    }
  }

  customElements.define('sticky-header', StickyHeader);
})();
